import React from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  HiOutlineSparkles,
  HiOutlineCalendar,
  HiOutlineChartBar,
  HiOutlineUserGroup,
  HiOutlineDeviceMobile,
  HiOutlineHeart
} from 'react-icons/hi'
import { FiArrowRight, FiArrowUpRight } from 'react-icons/fi'

/* ============================================================
 *  YUNYUN Tech. / Home
 *  高級柔美 × Apple Bento × 1440+ optimised
 * ============================================================ */

const Page = styled.div`
  background: var(--color-bg);
  min-height: 100vh;
  color: var(--color-ink);
`

/* ---------- Hero ---------- */
const Hero = styled.section`
  position: relative;
  width: 100%;
  min-height: 92vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 5rem 1.5rem 2rem;

  @media (min-width: 1440px) {
    min-height: 88vh;
    padding: 5rem 2rem 2.5rem;
  }
`

const HeroVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
`

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(27, 13, 18, 0.55) 0%,
    rgba(213, 114, 138, 0.3) 55%,
    rgba(27, 13, 18, 0.6) 100%
  );
  z-index: 2;
`

const HeroInner = styled.div`
  position: relative;
  z-index: 3;
  max-width: var(--container-wide);
  width: 100%;
  padding: 5rem 0 3rem;
  color: #fff;
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 3.5rem;
  align-items: center;

  @media (min-width: 1440px) {
    grid-template-columns: 1.15fr 0.85fr;
    gap: 5rem;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    padding: 5rem 0 2rem;
    gap: 2.5rem;
  }
`

const Eyebrow = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.78rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--color-primary-soft);
  padding: 0.55rem 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: var(--radius-pill);
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.1);
  margin-bottom: 1.5rem;
`

const HeroTitle = styled(motion.h1)`
  font-family: 'Playfair Display', 'Noto Serif TC', serif;
  font-size: clamp(1.9rem, 5vw, 4.6rem);
  line-height: 1.08;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin-bottom: 1.5rem;

  em {
    font-style: italic;
    color: var(--color-primary-soft);
    font-weight: 500;
  }

  @media (min-width: 1440px) {
    font-size: 5rem;
  }
`

const HeroLead = styled(motion.p)`
  font-size: 1.12rem;
  line-height: 1.78;
  color: rgba(255, 255, 255, 0.92);
  max-width: 560px;
  margin-bottom: 2.2rem;

  @media (min-width: 1440px) {
    font-size: 1.2rem;
    max-width: 620px;
  }
`

const HeroActions = styled(motion.div)`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #fff;
  color: var(--color-ink);
  border: none;
  padding: 1rem 1.75rem;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.02em;
  cursor: pointer;
  border-radius: var(--radius-pill);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 42px rgba(0, 0, 0, 0.22);
  }
`

const GhostBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.55);
  padding: 1rem 1.75rem;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  border-radius: var(--radius-pill);
  backdrop-filter: blur(10px);
  transition: background 0.25s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
`

const HeroCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.96);
  color: var(--color-ink);
  padding: 2.25rem 2rem 2rem;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-heavy);
  border: 1px solid rgba(255, 255, 255, 0.5);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--color-primary), var(--color-primary-deep));
  }

  @media (min-width: 1440px) {
    padding: 2.75rem 2.4rem 2.25rem;
  }
`

const HeroCardTitle = styled.div`
  font-size: 0.75rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--color-muted);
  margin-bottom: 0.75rem;
  font-weight: 600;
`

const HeroCardMain = styled.div`
  font-family: 'Playfair Display', serif;
  font-size: 1.9rem;
  font-weight: 700;
  color: var(--color-ink);
  margin-bottom: 0.5rem;
  letter-spacing: -0.01em;
`

const HeroCardDesc = styled.p`
  font-size: 0.94rem;
  line-height: 1.68;
  color: var(--color-ink-soft);
  margin-bottom: 1.5rem;
`

const HeroCardStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--color-line);
`

const HeroStat = styled.div`
  strong {
    display: block;
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    color: var(--color-primary);
    font-weight: 700;
    line-height: 1;
    margin-bottom: 0.3rem;
  }
  span {
    font-size: 0.72rem;
    color: var(--color-muted);
    letter-spacing: 0.06em;
  }
`

/* ---------- Brand Band ---------- */
const BrandBand = styled.section`
  padding: 2rem 1.5rem;
`

const BrandInner = styled.div`
  max-width: var(--container-wide);
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1.5rem;
  background: #fff;
  border: 1px solid var(--color-line);
  border-radius: var(--radius-xl);
  padding: 1.5rem 2.25rem;
  box-shadow: var(--shadow-card);

  p {
    font-size: 0.78rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--color-muted);
    font-weight: 600;
  }

  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem;
    border-radius: var(--radius-lg);
  }
`

const BrandPills = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  span {
    padding: 0.45rem 1rem;
    border: 1px solid var(--color-line);
    border-radius: var(--radius-pill);
    font-size: 0.85rem;
    color: var(--color-muted);
    font-weight: 500;
    background: var(--color-bg);
  }
`

/* ---------- Generic Section ---------- */
const Section = styled.section`
  padding: 3.5rem 1.5rem;
  max-width: var(--container-wide);
  margin: 0 auto;

  @media (min-width: 1440px) {
    padding: 4rem 2rem;
  }

  @media (max-width: 768px) {
    padding: 2.5rem 1.25rem;
  }
`

const SectionHead = styled.div`
  max-width: 760px;
  margin-bottom: 2.25rem;

  @media (max-width: 768px) {
    margin-bottom: 1.75rem;
  }

  .tag {
    display: inline-block;
    font-size: 0.72rem;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: var(--color-primary);
    font-weight: 700;
    margin-bottom: 1rem;
  }

  h2 {
    font-family: 'Playfair Display', 'Noto Serif TC', serif;
    font-size: clamp(1.55rem, 3.5vw, 3rem);
    line-height: 1.15;
    color: var(--color-ink);
    font-weight: 700;
    margin-bottom: 1rem;
    letter-spacing: -0.01em;
  }

  p {
    font-size: 1.05rem;
    line-height: 1.75;
    color: var(--color-muted);
  }

  @media (min-width: 1440px) {
    h2 { font-size: 3.2rem; }
    p { font-size: 1.12rem; }
  }
`

const SwipeHint = styled.div`
  display: none;

  @media (max-width: 640px) {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.72rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--color-primary);
    font-weight: 600;
    margin-top: -0.75rem;
    margin-bottom: 0.25rem;

    .arrow {
      font-size: 1rem;
      letter-spacing: 0;
    }
  }
`

/* ---------- Bento Feature Grid ---------- */
const BentoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-auto-rows: minmax(220px, auto);
  gap: 1.25rem;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);

    .bento-scroll > *:last-child {
      grid-column: span 2;
    }
  }

  @media (max-width: 640px) {
    display: block;
  }
`

/* Small cards: part of the grid on desktop, a swipe row on mobile */
const BentoScroller = styled.div`
  display: contents;

  @media (max-width: 640px) {
    display: flex;
    align-items: stretch;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    gap: 0.9rem;
    padding: 0.5rem 1.25rem 1.5rem;
    margin: 0 -1.25rem;
    scroll-padding-inline: 1.25rem;

    &::after {
      content: '';
      flex: 0 0 0.35rem;
    }

    &::-webkit-scrollbar {
      display: none;
    }
    scrollbar-width: none;
  }
`

const BentoCard = styled(motion.div)`
  grid-column: span ${(p) => p.col || 2};
  grid-row: span ${(p) => p.row || 1};
  background: #fff;
  border: 1px solid var(--color-line);
  border-radius: var(--radius-lg);
  padding: 2rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-card-hover);
    border-color: var(--color-primary);
  }

  .icon {
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, var(--color-bg-soft), var(--color-primary-soft));
    color: var(--color-primary);
    border-radius: var(--radius-md);
  }

  h4 {
    font-size: 1.2rem;
    font-weight: 700;
    color: var(--color-ink);
    line-height: 1.35;
    margin-top: auto;
  }

  p {
    font-size: 0.95rem;
    line-height: 1.7;
    color: var(--color-muted);
  }

  @media (max-width: 960px) {
    grid-column: span 1;
    grid-row: span 1;
    padding: 1.75rem;
  }

  @media (max-width: 640px) {
    h4 {
      margin-top: 0.5rem;
    }
    flex: 0 0 78%;
    max-width: 300px;
    min-height: 240px;
    scroll-snap-align: start;
    padding: 1.6rem;
  }
`

const BentoFeatured = styled(BentoCard)`
  background: linear-gradient(135deg, #1b0d12 0%, #3d1f2a 100%);
  color: #fff;
  border-color: #1b0d12;
  min-height: 280px;

  @media (max-width: 960px) {
    grid-column: span 2;
  }

  @media (max-width: 640px) {
    flex: none;
    max-width: none;
    width: 100%;
    min-height: 0;
    margin-bottom: 1.5rem;
  }

  .icon {
    background: rgba(245, 198, 211, 0.18);
    color: var(--color-primary-soft);
  }

  h4 {
    color: #fff;
    font-family: 'Playfair Display', serif;
    font-size: 1.6rem;
    font-weight: 700;

    @media (max-width: 640px) {
      font-size: 1.3rem;
    }
  }

  p {
    color: rgba(255, 255, 255, 0.78);
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.35rem 0.85rem;
    background: rgba(245, 198, 211, 0.18);
    border-radius: var(--radius-pill);
    font-size: 0.72rem;
    letter-spacing: 0.14em;
    color: var(--color-primary-soft);
    width: fit-content;
    font-weight: 600;
    text-transform: uppercase;
  }

  &:hover {
    border-color: var(--color-primary);
  }
`

/* ---------- Showcase bento ---------- */
const ShowcaseBento = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const ShowcaseMedia = styled.div`
  position: relative;
  min-height: 520px;
  background: url('/images/hero.webp') center/cover no-repeat,
    var(--color-primary-soft);
  border-radius: var(--radius-xl);
  overflow: hidden;
  border: 1px solid var(--color-line);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 50%, rgba(27, 13, 18, 0.35) 100%);
  }

  @media (max-width: 900px) {
    min-height: 360px;
    border-radius: var(--radius-lg);
  }
`

const ShowcaseBadge = styled.div`
  position: absolute;
  bottom: 1.5rem;
  left: 1.5rem;
  background: rgba(255, 255, 255, 0.95);
  padding: 0.65rem 1.1rem;
  font-size: 0.78rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--color-ink);
  font-weight: 700;
  z-index: 2;
  border-radius: var(--radius-pill);
`

const ShowcaseText = styled.div`
  background: #fff;
  border: 1px solid var(--color-line);
  border-radius: var(--radius-xl);
  padding: 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: var(--shadow-card);

  .tag {
    display: inline-block;
    font-size: 0.72rem;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: var(--color-primary);
    font-weight: 700;
    margin-bottom: 1rem;
  }
  h3 {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.4rem, 3vw, 2.4rem);
    line-height: 1.25;
    color: var(--color-ink);
    margin-bottom: 1.25rem;
    font-weight: 700;
  }
  p {
    font-size: 1rem;
    line-height: 1.8;
    color: var(--color-ink-soft);
    margin-bottom: 1.25rem;
  }

  @media (max-width: 900px) {
    padding: 2rem;
    border-radius: var(--radius-lg);
  }
`

const CheckList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1.25rem 0 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  li {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    font-size: 0.98rem;
    color: var(--color-ink);
    line-height: 1.6;

    @media (max-width: 640px) {
      font-size: 0.9rem;
    }

    &::before {
      content: '';
      display: inline-block;
      width: 8px;
      height: 8px;
      background: var(--color-primary);
      margin-top: 0.6rem;
      flex-shrink: 0;
      border-radius: 50%;
    }
  }
`

const InlineLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--color-ink);
  color: #fff;
  border: none;
  padding: 0.9rem 1.5rem;
  border-radius: var(--radius-pill);
  font-weight: 600;
  font-size: 0.92rem;
  cursor: pointer;
  letter-spacing: 0.03em;
  width: fit-content;
  transition: background 0.25s ease;

  &:hover {
    background: var(--color-primary);
  }
`

/* ---------- Audience bento ---------- */
const AudienceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    display: flex;
    grid-template-columns: none;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    gap: 0.9rem;
    padding: 0.5rem 1.25rem 1.5rem;
    margin: 0 -1.25rem;
    scroll-padding-inline: 1.25rem;

    &::after {
      content: '';
      flex: 0 0 0.35rem;
    }

    &::-webkit-scrollbar {
      display: none;
    }
    scrollbar-width: none;
  }
`

const AudienceCard = styled.div`
  background: ${(p) =>
    p.accent
      ? 'linear-gradient(135deg, #d5728a 0%, #b85578 100%)'
      : '#fff'};
  color: ${(p) => (p.accent ? '#fff' : 'var(--color-ink)')};
  border: 1px solid ${(p) => (p.accent ? 'transparent' : 'var(--color-line)')};
  border-radius: var(--radius-lg);
  padding: 2rem 1.75rem;
  text-align: left;
  transition: all 0.3s ease;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 1rem;

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-card-hover);
  }

  @media (max-width: 640px) {
    flex: 0 0 68%;
    max-width: 260px;
    scroll-snap-align: start;
    min-height: 180px;
  }

  .num {
    white-space: nowrap;
    font-size: 0.72rem;
    letter-spacing: 0.2em;
    color: ${(p) =>
      p.accent ? 'rgba(255,255,255,0.7)' : 'var(--color-primary)'};
    font-weight: 700;
  }

  h5 {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    color: ${(p) => (p.accent ? '#fff' : 'var(--color-ink)')};
    font-weight: 700;
  }
  p {
    font-size: 0.88rem;
    color: ${(p) =>
      p.accent ? 'rgba(255,255,255,0.85)' : 'var(--color-muted)'};
    line-height: 1.6;
  }
`

/* ---------- Trend dark bento ---------- */
const TrendOuter = styled.section`
  padding: 3.5rem 1.5rem;

  @media (min-width: 1440px) {
    padding: 4rem 2rem;
  }
  @media (max-width: 768px) {
    padding: 2.5rem 1.25rem;
  }
`

const TrendWrap = styled.div`
  background: linear-gradient(160deg, #1b0d12 0%, #3d1f2a 100%);
  color: #fff;
  padding: 4rem 3rem;
  border-radius: var(--radius-xl);
  max-width: var(--container-wide);
  margin: 0 auto;
  box-shadow: var(--shadow-heavy);

  @media (max-width: 768px) {
    padding: 2.25rem 1.25rem;
    border-radius: var(--radius-lg);
  }
`

const TrendInner = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 2rem;
  }
`

const TrendHead = styled.div`
  .tag {
    display: inline-block;
    font-size: 0.72rem;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: var(--color-primary-soft);
    font-weight: 700;
    margin-bottom: 1rem;
  }
  h2 {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.55rem, 3.5vw, 2.8rem);
    line-height: 1.2;
    font-weight: 700;
    margin-bottom: 1.25rem;
    color: #fff;
  }
  p {
    font-size: 1.02rem;
    line-height: 1.8;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 1.75rem;
  }
`

const TrendStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 640px) {
    gap: 0.6rem;
  }
`

const TrendStat = styled.div`
  padding: 1.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(245, 198, 211, 0.18);
  border-radius: var(--radius-lg);
  backdrop-filter: blur(8px);

  .num {
    white-space: nowrap;
    font-family: 'Playfair Display', serif;
    font-size: 2.4rem;
    font-weight: 700;
    color: var(--color-primary-soft);
    line-height: 1;
    margin-bottom: 0.5rem;
    letter-spacing: -0.01em;
  }
  .label {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.75);
    line-height: 1.55;
  }

  @media (max-width: 640px) {
    padding: 1.1rem 0.95rem;
    border-radius: var(--radius-md);

    .num {
      font-size: 1.55rem;
      margin-bottom: 0.4rem;
    }
    .label {
      font-size: 0.75rem;
      line-height: 1.5;
    }
  }
`

const TrendBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #fff;
  color: var(--color-ink);
  border: none;
  padding: 0.9rem 1.6rem;
  font-weight: 700;
  font-size: 0.92rem;
  cursor: pointer;
  border-radius: var(--radius-pill);
  transition: transform 0.25s ease;

  &:hover {
    transform: translateY(-2px);
  }
`

/* ---------- Press bento ---------- */
const PressBento = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 1.25rem;
  min-height: 560px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    grid-template-rows: none;
    min-height: auto;
  }

  @media (max-width: 640px) {
    display: block;
  }
`

const PressScroller = styled.div`
  display: contents;

  @media (max-width: 640px) {
    display: flex;
    align-items: stretch;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    gap: 0.9rem;
    padding: 0.5rem 1.25rem 1.5rem;
    margin: 0 -1.25rem;
    scroll-padding-inline: 1.25rem;

    &::after {
      content: '';
      flex: 0 0 0.35rem;
    }

    &::-webkit-scrollbar {
      display: none;
    }
    scrollbar-width: none;
  }
`

const PressLarge = styled.a`
  grid-column: 1;
  grid-row: 1 / 3;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 2.5rem;
  border-radius: var(--radius-xl);
  color: #fff;
  text-decoration: none;
  overflow: hidden;
  background-image:
    linear-gradient(
      180deg,
      rgba(27, 13, 18, 0.1) 0%,
      rgba(27, 13, 18, 0.55) 55%,
      rgba(27, 13, 18, 0.92) 100%
    ),
    url('https://www.popdaily.com.tw/shaper/u/202512/c4ff83a1-5929-4fc5-84fd-afb2d97587f1.jpeg?resize-w=1700&resize-h=884&format=webp');
  background-size: cover;
  background-position: center 20%;
  background-repeat: no-repeat;
  min-height: 480px;
  transition: transform 0.35s ease, box-shadow 0.35s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 80% 10%, rgba(245, 198, 211, 0.22) 0%, transparent 45%),
      linear-gradient(135deg, rgba(213, 114, 138, 0.2) 0%, transparent 60%);
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-heavy);
    background-position: center 15%;
  }

  .badge {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.4rem 0.9rem;
    background: rgba(255, 255, 255, 0.16);
    backdrop-filter: blur(10px);
    border-radius: var(--radius-pill);
    font-size: 0.72rem;
    letter-spacing: 0.18em;
    color: #fff;
    font-weight: 600;
    width: fit-content;
    margin-bottom: 1rem;
    text-transform: uppercase;
  }
  .meta {
    display: flex;
    gap: 0.75rem;
    font-size: 0.72rem;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--color-primary-soft);
    font-weight: 600;
    margin-bottom: 0.75rem;
    position: relative;
    z-index: 1;
  }
  h4 {
    position: relative;
    z-index: 1;
    font-family: 'Playfair Display', 'Noto Serif TC', serif;
    font-size: clamp(1.3rem, 2.4vw, 2rem);
    line-height: 1.3;
    color: #fff;
    margin-bottom: 1rem;
    font-weight: 700;
  }
  p {
    position: relative;
    z-index: 1;
    font-size: 0.98rem;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.88);
    margin-bottom: 1.5rem;
    max-width: 520px;
  }
  .link {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: #fff;
    font-weight: 600;
    font-size: 0.9rem;
    letter-spacing: 0.02em;
  }

  @media (max-width: 900px) {
    grid-column: 1;
    grid-row: auto;
    border-radius: var(--radius-lg);
    padding: 2rem;
    min-height: 360px;
  }

  @media (max-width: 640px) {
    min-height: 380px;
    padding: 1.75rem;
    margin-bottom: 1.5rem;
  }
`

const PressCard = styled.a`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  padding: 2rem;
  background: #fff;
  border: 1px solid var(--color-line);
  border-radius: var(--radius-lg);
  color: var(--color-ink);
  text-decoration: none;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-card-hover);
    border-color: var(--color-primary);
  }

  .meta {
    flex-wrap: wrap;
    row-gap: 0.25rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.7rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--color-muted);
    font-weight: 600;
  }
  .outlet {
    white-space: nowrap;
    color: var(--color-primary);
  }
  .date {
    white-space: nowrap;
  }
  .date::before {
    content: '·';
    margin-right: 0.75rem;
    color: var(--color-primary);
  }
  h4 {
    font-family: 'Playfair Display', 'Noto Serif TC', serif;
    font-size: 1.1rem;
    line-height: 1.4;
    color: var(--color-ink);
    font-weight: 700;
  }
  p {
    font-size: 0.88rem;
    line-height: 1.65;
    color: var(--color-muted);
    flex: 1;
  }
  .link {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: var(--color-primary);
    font-weight: 600;
    font-size: 0.85rem;
    letter-spacing: 0.02em;
  }

  @media (max-width: 640px) {
    flex: 0 0 78%;
    max-width: 290px;
    scroll-snap-align: start;
    min-height: 320px;
  }
`

/* ---------- CTA ---------- */
const CtaWrap = styled.section`
  max-width: var(--container-wide);
  margin: 0 auto;
  padding: 2rem 1.5rem 4rem;

  @media (min-width: 1440px) {
    padding: 2.5rem 2rem 5rem;
  }
`

const CtaInner = styled.div`
  background: linear-gradient(135deg, #d5728a 0%, #b85578 100%);
  color: #fff;
  padding: 4rem 3rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  flex-wrap: wrap;
  position: relative;
  overflow: hidden;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-heavy);

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.18) 0%, transparent 70%);
    border-radius: 50%;
  }
  &::after {
    content: '';
    position: absolute;
    bottom: -40%;
    left: -5%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(245, 198, 211, 0.25) 0%, transparent 70%);
    border-radius: 50%;
  }

  @media (max-width: 768px) {
    padding: 2.75rem 2rem;
    border-radius: var(--radius-lg);
  }
`

const CtaText = styled.div`
  position: relative;
  z-index: 1;
  h3 {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.4rem, 3vw, 2.4rem);
    font-weight: 700;
    margin-bottom: 0.75rem;
    line-height: 1.2;
  }
  p {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.92);
    line-height: 1.65;
    max-width: 540px;
  }
`

const CtaBtn = styled.button`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #fff;
  color: var(--color-ink);
  border: none;
  padding: 1.05rem 2rem;
  border-radius: var(--radius-pill);
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: transform 0.25s ease;

  &:hover {
    transform: translateY(-2px);
  }
`

/* ============================================================
 *  Component
 * ============================================================ */

const Home = () => {
  const navigate = useNavigate()

  return (
    <Page>
      {/* Hero */}
      <Hero>
        <HeroVideo autoPlay loop muted playsInline>
          <source src='/images/bg-hero2.mp4' type='video/mp4' />
        </HeroVideo>
        <HeroOverlay />
        <HeroInner>
          <div>
            <Eyebrow
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <HiOutlineSparkles /> YUNYUN TECH. × BEAUTEFEEL
            </Eyebrow>
            <HeroTitle
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              以科技之筆，<br />
              重新書寫<em>美業預約體驗</em>
            </HeroTitle>
            <HeroLead
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
            >
              雲耘科技專注於打造美業 SaaS 解決方案。旗艦產品
              <strong> BEAUTEFEEL 美感線上預約平台</strong>
              ，已協助上百家美睫、美甲、美髮、SPA
              店家翻新預約流程，讓服務回歸專業本質。
            </HeroLead>
            <HeroActions
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <PrimaryBtn onClick={() => navigate('/services')}>
                了解 Beautefeel <FiArrowRight />
              </PrimaryBtn>
              <GhostBtn onClick={() => navigate('/trends')}>
                美業趨勢觀察 <FiArrowUpRight />
              </GhostBtn>
            </HeroActions>
          </div>

          <HeroCard
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
          >
            <HeroCardTitle>Flagship Product</HeroCardTitle>
            <HeroCardMain>BEAUTEFEEL</HeroCardMain>
            <HeroCardDesc>
              專為美業設計的雲端預約與顧客管理系統。整合排班、金流、顧客回訪、會員紀錄，
              讓店家像經營品牌一樣經營每一次預約。
            </HeroCardDesc>
            <HeroCardStats>
              <HeroStat>
                <strong>500+</strong>
                <span>合作店家</span>
              </HeroStat>
              <HeroStat>
                <strong>98%</strong>
                <span>預約完成率</span>
              </HeroStat>
              <HeroStat>
                <strong>24/7</strong>
                <span>雲端管理</span>
              </HeroStat>
            </HeroCardStats>
          </HeroCard>
        </HeroInner>
      </Hero>

      {/* Brand band */}
      <BrandBand>
        <BrandInner>
          <p>Serving Beauty Professionals</p>
          <BrandPills>
            <span>美睫 Eyelash</span>
            <span>美甲 Nail</span>
            <span>美髮 Hair</span>
            <span>做臉 Facial</span>
            <span>SPA 芳療</span>
            <span>醫美 Medical Aesthetic</span>
          </BrandPills>
        </BrandInner>
      </BrandBand>

      {/* Why Beautefeel — Bento */}
      <Section>
        <SectionHead>
          <span className='tag'>Why Beautefeel</span>
          <h2>
            讓專業美業，<br />
            擁有真正屬於自己的數位主場
          </h2>
          <p>
            我們相信每一位美業工作者都是獨立的品牌。Beautefeel
            不只是預約系統，而是從顧客走進品牌的第一印象到第 N
            次回訪，完整陪伴的數位夥伴。
          </p>
        </SectionHead>
        <BentoGrid>
          <BentoFeatured
            col={4}
            row={2}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className='chip'>Made in Taiwan</span>
            <div className='icon'>
              <HiOutlineSparkles size={24} />
            </div>
            <h4>
              為台灣美業量身打造，<br />
              懂在地節奏的 SaaS 夥伴
            </h4>
            <p>
              從 LINE 預約、訂金習慣、節慶檔期，到沙龍常見的輪班與抽成制度，
              Beautefeel 的每個設計決策，都來自與台灣上百家美業店家的長期對話。
              不是舶來品，是真正在你隔壁被用過、被調整、被信任的系統。
            </p>
          </BentoFeatured>

          <SwipeHint>
            <span>左右滑動查看</span>
            <span className='arrow'>→</span>
          </SwipeHint>

          <BentoScroller className='bento-scroll'>

          <BentoCard
            col={2}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.05 }}
          >
            <div className='icon'>
              <HiOutlineCalendar size={22} />
            </div>
            <h4>24 小時無人化預約</h4>
            <p>顧客深夜滑手機即可完成預約，店家無須再半夜回訊息。</p>
          </BentoCard>

          <BentoCard
            col={2}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className='icon'>
              <HiOutlineUserGroup size={22} />
            </div>
            <h4>顧客資料一鍵掌握</h4>
            <p>消費紀錄、偏好服務、過敏項目完整記錄，讓每位顧客都被記得。</p>
          </BentoCard>

          <BentoCard
            col={2}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <div className='icon'>
              <HiOutlineDeviceMobile size={22} />
            </div>
            <h4>品牌專屬預約頁</h4>
            <p>
              服務、價格、作品集、評價一頁呈現。整合 IG 連結，流量直接轉營收。
            </p>
          </BentoCard>

          <BentoCard
            col={2}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className='icon'>
              <HiOutlineChartBar size={22} />
            </div>
            <h4>營運數據儀表板</h4>
            <p>
              每月營收、熱門服務、尖峰時段、回客率一目瞭然，用數據做決策。
            </p>
          </BentoCard>

          <BentoCard
            col={2}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.25 }}
          >
            <div className='icon'>
              <HiOutlineHeart size={22} />
            </div>
            <h4>會員與回訪提醒</h4>
            <p>
              自動化生日優惠、回訪提醒、點數累積，把一次性顧客轉成長期粉絲。
            </p>
          </BentoCard>
          </BentoScroller>
        </BentoGrid>
      </Section>

      {/* Beautefeel Showcase */}
      <Section>
        <ShowcaseBento>
          <ShowcaseMedia>
            <ShowcaseBadge>Beautefeel · 旗艦產品</ShowcaseBadge>
          </ShowcaseMedia>
          <ShowcaseText>
            <span className='tag'>The Flagship</span>
            <h3>
              從預約、接待、到回訪，<br />
              一個系統搞定整段顧客旅程
            </h3>
            <p>
              Beautefeel 不是單一工具，而是整合<strong>預約</strong>、
              <strong>金流</strong>、<strong>會員</strong>、
              <strong>行銷</strong>的一站式營運中樞。讓美業店家不必再用
              Excel、LINE、表格三邊切換。
            </p>
            <CheckList>
              <li>智慧排班：多店、多位設計師、多服務同時排程不撞點</li>
              <li>線上收訂金：降低放鳥率最高 70%</li>
              <li>LINE/Email 雙通道自動提醒：客戶不再忘記預約</li>
              <li>顧客資料庫 GDPR 等級加密存放，安全合規</li>
            </CheckList>
            <InlineLink onClick={() => navigate('/services')}>
              完整產品介紹 <FiArrowRight />
            </InlineLink>
          </ShowcaseText>
        </ShowcaseBento>
      </Section>

      {/* Audience */}
      <Section>
        <SectionHead>
          <span className='tag'>Who We Serve</span>
          <h2>為各類型美業品牌量身打造</h2>
          <p>
            無論你是個人工作室、連鎖沙龍、還是新創醫美品牌，Beautefeel
            的彈性架構都能貼合你的營運節奏。
          </p>
        </SectionHead>
        <SwipeHint>
          <span>左右滑動查看</span>
          <span className='arrow'>→</span>
        </SwipeHint>
        <AudienceGrid>
          <AudienceCard>
            <span className='num'>01</span>
            <div>
              <h5>個人工作室</h5>
              <p>一人經營也能擁有品牌質感。</p>
            </div>
          </AudienceCard>
          <AudienceCard>
            <span className='num'>02</span>
            <div>
              <h5>多設計師沙龍</h5>
              <p>多位設計師獨立預約版面與排程。</p>
            </div>
          </AudienceCard>
          <AudienceCard accent>
            <span className='num'>03</span>
            <div>
              <h5>連鎖品牌</h5>
              <p>多分店統一後台，集中數據管理。</p>
            </div>
          </AudienceCard>
          <AudienceCard>
            <span className='num'>04</span>
            <div>
              <h5>醫美・SPA</h5>
              <p>高客單價顧客的細緻管理與 SOP。</p>
            </div>
          </AudienceCard>
        </AudienceGrid>
      </Section>

      {/* Trend preview */}
      <TrendOuter>
        <TrendWrap>
          <TrendInner>
            <TrendHead>
              <span className='tag'>Industry Insights</span>
              <h2>
                美業預約平台，<br />
                已成為產業標配
              </h2>
              <p>
                全球美業市場正加速數位化轉型。消費者期待的不只是好技術，而是從預約到售後的完整體驗。
                搭上這波浪潮的品牌，平均在 6 個月內就能看到顧客黏著度與回訪率顯著提升。
              </p>
              <TrendBtn onClick={() => navigate('/trends')}>
                閱讀完整趨勢報告 <FiArrowUpRight />
              </TrendBtn>
            </TrendHead>

            <TrendStats>
              <TrendStat>
                <div className='num'>$4,830億</div>
                <div className='label'>2024 全球美業市場規模（美金）</div>
              </TrendStat>
              <TrendStat>
                <div className='num'>67%</div>
                <div className='label'>消費者偏好線上預約，而非電話或私訊</div>
              </TrendStat>
              <TrendStat>
                <div className='num'>3.2x</div>
                <div className='label'>導入預約 SaaS 後，平均回客率提升倍數</div>
              </TrendStat>
              <TrendStat>
                <div className='num'>-42%</div>
                <div className='label'>線上收訂金後，放鳥率平均下降幅度</div>
              </TrendStat>
            </TrendStats>
          </TrendInner>
        </TrendWrap>
      </TrendOuter>

      {/* Press — Bento layout */}
      <Section>
        <SectionHead>
          <span className='tag'>Press & Media</span>
          <h2>媒體報導</h2>
          <p>
            Beautefeel 與雲耘科技的故事，正被各大媒體關注與報導。
            我們持續在女性創業、美業 SaaS、數位轉型等議題上被看見。
          </p>
        </SectionHead>
        <PressBento>
          <PressLarge
            href='https://www.popdaily.com.tw/forum/life/1586324'
            target='_blank'
            rel='noopener noreferrer'
          >
            <span className='badge'>Featured Coverage</span>
            <div className='meta'>
              <span>POPDaily 波波黛莉</span>
              <span>生活焦點專欄</span>
            </div>
            <h4>
              Beautefeel 美感預約——<br />
              從滑手機的那一刻起，開始被好好對待
            </h4>
            <p>
              POPDaily 波波黛莉專題報導，深入介紹 Beautefeel
              如何用設計語言重新定義美業預約體驗，讓「預約」本身成為女性消費者期待的品牌儀式。
            </p>
            <span className='link'>
              閱讀完整報導 <FiArrowUpRight />
            </span>
          </PressLarge>

          <SwipeHint>
            <span>左右滑動查看</span>
            <span className='arrow'>→</span>
          </SwipeHint>

          <PressScroller>

          <PressCard
            href='https://woman.udn.com/woman/story/123163/8600011'
            target='_blank'
            rel='noopener noreferrer'
          >
            <div className='meta'>
              <span className='outlet'>UDN · 女人會客室</span>
              <span className='date'>聯合新聞網專訪</span>
            </div>
            <h4>從女性創業者的視角，看見美業平台如何被重新打造</h4>
            <p>
              聯合新聞網「女人會客室」專欄報導 Beautefeel
              創辦團隊，探討女性科技創業如何切入美業市場。
            </p>
            <span className='link'>
              閱讀報導 <FiArrowUpRight />
            </span>
          </PressCard>

          <PressCard
            href='https://news.ebc.net.tw/news/living/473434'
            target='_blank'
            rel='noopener noreferrer'
          >
            <div className='meta'>
              <span className='outlet'>EBC 東森新聞</span>
              <span className='date'>產業焦點報導</span>
            </div>
            <h4>美業預約平台新勢力，Beautefeel 翻轉沙龍經營模式</h4>
            <p>
              東森新聞聚焦台灣美業數位轉型浪潮，報導 Beautefeel
              如何協助在地美業店家走向品牌化經營。
            </p>
            <span className='link'>
              閱讀報導 <FiArrowUpRight />
            </span>
          </PressCard>
          </PressScroller>
        </PressBento>
      </Section>

      {/* CTA */}
      <CtaWrap>
        <CtaInner>
          <CtaText>
            <h3>準備讓你的品牌進入下一個階段？</h3>
            <p>
              預約一對一線上諮詢，由我們的美業顧問分析你目前的營運狀態，
              為你量身建議合適的導入方案。
            </p>
          </CtaText>
          <CtaBtn onClick={() => navigate('/contact')}>
            立即預約諮詢 <FiArrowRight />
          </CtaBtn>
        </CtaInner>
      </CtaWrap>
    </Page>
  )
}

export default Home
